import { useRef, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ImageLightbox } from '../components/ImageLightbox';
import { ImageWithSkeleton } from '../components/ImageWithSkeleton';
import { LandingHeader } from '../components/LandingHeader';
import { LandingFooter } from '../components/LandingFooter';
import { Reveal } from '../components/Reveal';
import { SectionWave } from '../components/SectionWave';
import { Pricing } from '../components/Pricing';
import { useParallax } from '../hooks/useParallax';
import { useTheme } from '../contexts/ThemeContext';
import {
  Accordion as AccordionBase,
  AccordionItem as AccordionItemBase,
  AccordionTrigger as AccordionTriggerBase,
  AccordionContent as AccordionContentBase,
} from '../components/ui/accordion';

// accordion.tsx é `// @ts-nocheck` sem generics no forwardRef, então o TS
// infere props vazias pra quem o usa — cast local pra desbloquear (mesmo
// padrão de outros componentes ui/* no projeto).
const Accordion = AccordionBase as any;
const AccordionItem = AccordionItemBase as any;
const AccordionTrigger = AccordionTriggerBase as any;
const AccordionContent = AccordionContentBase as any;

import { SOLUTIONS } from '../data/solutions';
import { SPECIALTIES, HOME_FAQ } from '../data/content';
import { ShieldCheck, Mail, Layers, Headset, ArrowRight, ChevronDown } from 'lucide-react';

const PILLARS = [
  {
    icon: Layers,
    title: 'Tudo Integrado',
    description: 'Agenda, prontuário, financeiro e faturamento conversando entre si, sem planilha solta ou sistema paralelo.',
  },
  {
    icon: ShieldCheck,
    title: 'Feito Sob Medida',
    description: 'Cada clínica tem seu próprio espaço, com dados completamente isolados das demais e permissões por perfil.',
  },
  {
    icon: Headset,
    title: 'Suporte Dedicado',
    description: 'Time disponível pra implantar, treinar sua equipe e resolver o que precisar no dia a dia.',
  },
];

// Espelha o catálogo de backend/domain/plans.py — mudou o preço/período lá, muda aqui
// também. Os três planos têm os mesmos recursos; a diferença é só a cadência de
// cobrança (compromisso mais longo sai mais barato por mês equivalente).
const PLAN_FEATURES = [
  'Profissionais ilimitados',
  'Agenda',
  'Cadastro de pacientes e prontuário',
  'Financeiro e relatórios',
  'TISS/TUSS',
];

const PRICING_PLANS = [
  {
    name: 'Bronze',
    price: 99.90,
    period: 'mês',
    features: PLAN_FEATURES,
    description: 'Cobrança mensal, cancele quando quiser.',
    buttonText: 'Assinar Bronze',
    href: '/cadastro?plan=bronze',
    isPopular: true,
  },
  {
    name: 'Prata',
    price: 249.90,
    period: 'trimestre',
    features: PLAN_FEATURES,
    description: 'Cobrança trimestral, sai mais barato por mês.',
    buttonText: 'Assinar Prata',
    href: '/cadastro?plan=prata',
    isPopular: false,
  },
  {
    name: 'Ouro',
    price: 899.90,
    period: 'ano',
    features: PLAN_FEATURES,
    description: 'Cobrança anual, o melhor preço por mês.',
    buttonText: 'Assinar Ouro',
    href: '/cadastro?plan=ouro',
    isPopular: false,
  },
];

// Padrão de pontinhos usado como textura decorativa nas seções escuras,
// no lugar das imagens de fundo do Freepik que a Cloudflare bloqueou.
// Alpha já embutido na cor (em vez de um wrapper com opacity-*), senão o
// resultado composto fica praticamente invisível.
const dotPatternStyle = {
  backgroundImage: 'radial-gradient(rgba(255,255,255,0.22) 1.5px, transparent 1.5px)',
  backgroundSize: '26px 26px',
};

// Fade nas bordas do carrossel de especialidades, pra ele "nascer" e
// "sumir" suavemente em vez de cortar o texto de forma abrupta.
const marqueeMaskStyle = {
  maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
  WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
};

// Tilt 3D leve na imagem do hero, seguindo o mouse — só desktop (o handler
// não é chamado em telas touch, então em mobile fica só a flutuação).
const useTilt = (max = 8) => {
  const ref = useRef<HTMLDivElement>(null);
  const [tiltStyle, setTiltStyle] = useState<React.CSSProperties>({});

  const onMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const node = ref.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    setTiltStyle({
      transform: `perspective(1200px) rotateY(${(px - 0.5) * max}deg) rotateX(${-(py - 0.5) * max}deg) scale3d(1.03,1.03,1.03)`,
    });
  };

  const onMouseLeave = () => {
    setTiltStyle({ transform: 'perspective(1200px) rotateY(0deg) rotateX(0deg) scale3d(1,1,1)' });
  };

  return { ref, tiltStyle, onMouseMove, onMouseLeave };
};

export const LandingPage = () => {
  const tilt = useTilt();
  const scrollY = useParallax();
  const { theme } = useTheme();
  const notebookImage = theme === 'dark' ? '/assets/images/notebookdark.png' : '/assets/images/notebooklight.png';

  return (
    <div className="min-h-screen bg-background text-foreground">
      <LandingHeader />

      <main className="pt-16">
        {/* Hero */}
        <section
          className="relative overflow-hidden bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/images/hero.jpg')" }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#052e38]/95 via-[#063a46]/90 to-[#0a5f76]/80" />
          {/* Grão de ruído sutil, tira o efeito "gradiente liso demais" */}
          <div className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay bg-noise" />
          {/* Blobs animados dando profundidade ao fundo escuro — o parallax vai
              no wrapper posicionado (que também é quem define local/tamanho),
              e o filho preenche via inset-0 com a animation própria, porque
              animation sempre vence um transform inline no mesmo elemento */}
          <div
            className="pointer-events-none absolute -top-24 -left-24 w-96 h-96"
            style={{ transform: `translateY(${scrollY * 0.07}px)` }}
          >
            <div className="absolute inset-0 rounded-full bg-[#0ba5c6]/30 blur-3xl animate-blob" />
          </div>
          <div
            className="pointer-events-none absolute top-1/3 -right-20 w-80 h-80"
            style={{ transform: `translateY(${scrollY * -0.05}px)` }}
          >
            <div className="absolute inset-0 rounded-full bg-[#0a5f76]/40 blur-3xl animate-blob [animation-delay:4s]" />
          </div>

          <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Reveal>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-bold uppercase tracking-wider mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0ba5c6] animate-pulse" />
                  Sistema Neurobit
                </div>
              </Reveal>
              <Reveal delay={80}>
                <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-6 leading-[1.1] text-white">
                  A gestão da sua clínica,{' '}
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#0ba5c6] via-white to-[#0ba5c6] bg-[length:200%_auto] animate-gradient-x">
                    simplificada e eficiente
                  </span>
                </h1>
              </Reveal>
              <Reveal delay={160}>
                <p className="text-lg text-white/80 mb-8">
                  Agenda, prontuário, financeiro e faturamento TISS em uma plataforma única —
                  pra clínicas de saúde que querem crescer com organização, não com planilha.
                </p>
              </Reveal>
              <Reveal delay={240}>
                <div className="flex flex-wrap gap-3">
                  <a href="mailto:contato@sistemaneurobit.com.br">
                    <Button size="lg" className="bg-white text-[#052e38] hover:bg-white/90 transition-transform hover:-translate-y-0.5 hover:shadow-lg hover:shadow-white/10">
                      <Mail className="mr-2 h-4 w-4" />
                      Quero conhecer
                    </Button>
                  </a>
                  <a href="https://app.sistemaneurobit.com.br">
                    <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white transition-transform hover:-translate-y-0.5">
                      Já sou cliente
                    </Button>
                  </a>
                </div>
              </Reveal>
            </div>
            <Reveal delay={200}>
              <div className="relative" style={{ transform: `translateY(${scrollY * 0.12}px)` }}>
                {/* Brilho ambiente pulsando atrás do notebook, dando profundidade */}
                <div className="pointer-events-none absolute inset-8 rounded-[2rem] bg-[#0ba5c6]/30 blur-3xl animate-pulse" />
                <div className="animate-float-slow">
                  <div
                    ref={tilt.ref}
                    onMouseMove={tilt.onMouseMove}
                    onMouseLeave={tilt.onMouseLeave}
                    style={{ ...tilt.tiltStyle, transition: 'transform 0.2s ease-out' }}
                    className="relative drop-shadow-2xl will-change-transform"
                  >
                    <ImageLightbox src="/assets/images/screenshots/dashboard.png" alt="Dashboard do Sistema Neurobit">
                      <ImageWithSkeleton
                        key={notebookImage}
                        src={notebookImage}
                        alt="Sistema Neurobit rodando em um notebook"
                        className="w-full h-auto"
                      />
                    </ImageLightbox>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <a
            href="#sobre"
            className="hidden sm:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-1 text-white/50 hover:text-white/80 transition-colors animate-bounce"
          >
            <span className="text-[10px] uppercase tracking-widest">Saiba mais</span>
            <ChevronDown size={18} />
          </a>

          <SectionWave />
        </section>

        {/* Pilares / Sobre */}
        <section id="sobre" className="relative overflow-hidden bg-muted/30">
          <div
            className="absolute inset-0 opacity-[0.4] dark:opacity-[0.15]"
            style={{
              backgroundImage: 'radial-gradient(currentColor 1px, transparent 1px)',
              backgroundSize: '22px 22px',
              color: 'var(--border)',
            }}
          />
          <div className="relative max-w-6xl mx-auto px-6 py-20">
            <Reveal className="text-center mb-12 max-w-xl mx-auto">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0ba5c6] mb-2">Por que o Neurobit</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold">Um sistema pensado para clínica de verdade</h2>
            </Reveal>
            <div className="grid sm:grid-cols-3 gap-6">
              {PILLARS.map((p, i) => (
                <Reveal key={p.title} delay={i * 100}>
                  <div className="group h-full rounded-2xl border bg-[hsl(var(--card)/0.7)] backdrop-blur-md text-card-foreground shadow p-6 text-center transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:border-[#0ba5c6]/40">
                    <div className="w-12 h-12 rounded-xl bg-[#0ba5c6]/10 flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:bg-[#0ba5c6]/20">
                      <p.icon className="text-[#0ba5c6]" size={22} />
                    </div>
                    <h3 className="font-bold mb-2">{p.title}</h3>
                    <p className="text-sm text-muted-foreground">{p.description}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Especialidades */}
        <section className="border-t border-border overflow-hidden">
          <div className="max-w-6xl mx-auto px-6 py-14">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0ba5c6] mb-6 text-center">Feito para diferentes especialidades</p>
            </Reveal>
          </div>
          <Reveal>
            <div className="relative w-full overflow-hidden pb-2" style={marqueeMaskStyle}>
              <div className="marquee-track animate-marquee flex w-max gap-3">
                {[...SPECIALTIES, ...SPECIALTIES].map((s, i) => (
                  <span
                    key={`${s}-${i}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border bg-card text-sm font-medium whitespace-nowrap transition-colors hover:border-[#0ba5c6]/50 hover:text-[#0ba5c6]"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0ba5c6]" />
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </section>

        {/* Recursos */}
        <section id="recursos" className="border-t border-border bg-muted/30">
          <div className="max-w-6xl mx-auto px-6 py-20">
            <Reveal className="text-center mb-12 max-w-xl mx-auto">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0ba5c6] mb-2">Recursos</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold">Tudo que sua clínica precisa</h2>
            </Reveal>
            <div className="flex flex-wrap justify-center gap-5">
              {SOLUTIONS.map((s, i) => (
                <Reveal
                  key={s.slug}
                  delay={(i % 3) * 100}
                  className="w-full sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)]"
                >
                  <Link
                    to={`/solucoes/${s.slug}`}
                    className="group relative block h-full overflow-hidden rounded-xl border bg-card text-card-foreground shadow p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-[#0ba5c6]/40"
                  >
                    {/* Reflexo de luz que passa pela carta ao passar o mouse */}
                    <span className="pointer-events-none absolute inset-y-0 left-0 w-1/3 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine dark:via-white/10" />
                    <s.icon className="h-7 w-7 text-[#0ba5c6] mb-4 transition-transform duration-300 group-hover:scale-110" />
                    <h3 className="font-bold mb-1.5">{s.title}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{s.tagline}</p>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#0ba5c6] opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0">
                      Saiba mais
                      <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Planos */}
        <section id="planos" className="border-t border-border">
          <Pricing
            plans={PRICING_PLANS}
            title="Um plano só, tudo incluso"
            description="Sem pegadinha de nível ou limite de profissionais — um preço único com acesso completo à plataforma."
          />
        </section>

        {/* FAQ */}
        <section className="border-t border-border bg-muted/30">
          <div className="max-w-3xl mx-auto px-6 py-20">
            <Reveal className="text-center mb-10">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0ba5c6] mb-2">Dúvidas frequentes</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold">Perguntas sobre o Sistema Neurobit</h2>
            </Reveal>
            <Reveal delay={100}>
              <Accordion type="single" collapsible className="rounded-2xl border bg-card divide-y">
                {HOME_FAQ.map((item, i) => (
                  <AccordionItem key={item.q} value={`item-${i}`} className="border-b-0 px-5">
                    <AccordionTrigger className="text-left font-semibold hover:no-underline">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>

        {/* CTA final */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#052e38] via-[#063a46] to-[#0a5f76]">
          <SectionWave flip />
          <div className="absolute inset-0" style={dotPatternStyle} />
          <div className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay bg-noise" />
          <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[32rem] h-[32rem] rounded-full bg-[#0ba5c6]/20 blur-3xl animate-blob" />
          <Reveal>
            <div className="relative max-w-6xl mx-auto px-6 py-16 text-center">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">Pronto pra organizar sua clínica?</h2>
              <p className="text-white/80 mb-8 max-w-xl mx-auto">
                Fala com a gente e veja como o Sistema Neurobit pode entrar na sua rotina.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <a href="mailto:contato@sistemaneurobit.com.br">
                  <Button size="lg" className="bg-white text-[#052e38] hover:bg-white/90 transition-transform hover:-translate-y-0.5 hover:shadow-lg hover:shadow-white/10">
                    <Mail className="mr-2 h-4 w-4" />
                    Fale conosco
                  </Button>
                </a>
                <a href="#recursos">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white transition-transform hover:-translate-y-0.5">
                    Conheça nossas soluções
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <LandingFooter />
    </div>
  );
};
